import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Users, CalendarDays, Receipt, FileText, BarChart3,
  Settings, LogOut, X, ChevronRight, Moon, Sun, ClipboardList, Wallet
} from 'lucide-react';
import { useAuthStore } from '../../store/auth.store';
import { useUIStore } from '../../store/ui.store';
import { useThemeStore } from '../../store/theme.store';

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  color: string;
  adminOnly?: boolean;
}

const allNavItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'text-purple-500' },
  { path: '/students', label: 'Students', icon: Users, color: 'text-electric-500' },
  { path: '/batches', label: 'Batches', icon: CalendarDays, color: 'text-cyan-500' },
  { path: '/teachers', label: 'Teachers', icon: Users, color: 'text-violet-500', adminOnly: true },
  { path: '/fees', label: 'Fees', icon: Receipt, color: 'text-gold-500' },
  { path: '/attendance', label: 'Attendance', icon: CalendarDays, color: 'text-lime-600' },
  { path: '/expenses', label: 'Expenses', icon: Wallet, color: 'text-rose-500', adminOnly: true },
  { path: '/revenue', label: 'Revenue', icon: BarChart3, color: 'text-purple-500', adminOnly: true },
  { path: '/documents', label: 'Documents', icon: FileText, color: 'text-electric-400' },
  { path: '/reports', label: 'Reports', icon: BarChart3, color: 'text-tangerine-400' },
  { path: '/settings', label: 'Settings', icon: Settings, color: 'text-purple-400', adminOnly: true },
];

const bottomNavItems = [
  { path: '/dashboard', label: 'Home', icon: LayoutDashboard },
  { path: '/modules/students/classes', label: 'Students', icon: Users },
  { path: '/modules/exams/classes', label: 'Exam', icon: ClipboardList },
  { path: '/attendance', label: 'Attendance', icon: CalendarDays },
  { path: 'menu', label: 'More', icon: X },
];

export default function MobileNav() {
  const [showMenu, setShowMenu] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { modalOpen } = useUIStore();
  const { resolvedTheme, toggleTheme } = useThemeStore();

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-light-surface/95 dark:bg-dark-surface/95 backdrop-blur-lg border-t border-light-border dark:border-dark-border z-50 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around h-16 px-2">
          {bottomNavItems.map((item) => {
            const isMenu = item.path === 'menu';
            const isActive = !isMenu && (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`));
            
            return (
              <button
                key={item.path}
                onClick={() => isMenu ? setShowMenu(true) : null}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 px-3 rounded-xl min-w-[64px] min-h-[44px] transition-all active:scale-95 ${
                  isMenu 
                    ? 'text-light-text-muted dark:text-dark-text-muted' 
                    : isActive 
                      ? 'text-purple-600 dark:text-purple-400' 
                      : 'text-light-text-muted dark:text-dark-text-muted'
                }`}
              >
                {isMenu ? (
                  <div className="flex flex-col items-center gap-0.5">
                    <div className="w-5 h-5 flex flex-col justify-center gap-1">
                      <div className="w-4 h-0.5 bg-current rounded-full" />
                      <div className="w-3 h-0.5 bg-current rounded-full" />
                      <div className="w-4 h-0.5 bg-current rounded-full" />
                    </div>
                    <span className="text-[10px] font-medium leading-none">More</span>
                  </div>
                ) : (
                  <Link to={item.path} className="flex flex-col items-center gap-0.5">
                    <item.icon className={`w-5 h-5 ${isActive ? 'text-purple-500' : ''}`} />
                    <span className="text-[10px] font-medium leading-none">{item.label}</span>
                  </Link>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Full Menu Overlay */}
      <AnimatePresence>
        {showMenu && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
              onClick={() => setShowMenu(false)}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-[70] bg-light-surface dark:bg-dark-surface rounded-t-3xl max-h-[80vh] overflow-y-auto"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              {/* Handle bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-12 h-1 rounded-full bg-light-border dark:bg-dark-border" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-light-border dark:border-dark-border">
                <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Menu</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleTheme}
                    className="p-2 rounded-lg bg-light-elevated dark:bg-dark-elevated text-light-text dark:text-dark-text"
                    aria-label="Toggle theme"
                  >
                    {resolvedTheme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5" />}
                  </button>
                  <button 
                    onClick={() => setShowMenu(false)}
                    className="p-2 rounded-lg hover:bg-light-elevated dark:hover:bg-dark-elevated"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Menu Grid */}
              <div className="p-4 grid grid-cols-3 gap-3">
                {allNavItems.map((item) => {
                  if (item.adminOnly && user?.role !== 'admin') return null;
                  const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setShowMenu(false)}
                      className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-all active:scale-95 ${
                        isActive
                          ? 'bg-purple-50 dark:bg-purple-500/15 text-purple-600 dark:text-purple-400'
                          : 'bg-light-elevated dark:bg-dark-elevated text-light-text dark:text-dark-text hover:bg-light-border dark:hover:bg-dark-border/50'
                      }`}
                    >
                      <item.icon className={`w-6 h-6 ${isActive ? item.color : 'text-light-text-muted dark:text-dark-text-muted'}`} />
                      <span className="text-xs font-medium text-center leading-tight">{item.label}</span>
                    </Link>
                  );
                })}
              </div>

              {/* Logout */}
              <div className="px-4 pb-4 pt-2 border-t border-light-border dark:border-dark-border">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 font-medium active:scale-95 transition-all"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
